import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Wallet, DollarSign, Coins, CheckCircle2, XCircle, Gamepad2, ShieldAlert } from "lucide-react";
import { api, fmtCoins, fmtUsd, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [convs, setConvs] = useState([]);
  const [spawnStats, setSpawnStats] = useState(null);
  const [agConvs, setAgConvs] = useState([]);
  const [agStats, setAgStats] = useState(null);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/payouts").then((r) => setPayouts(r.data)).catch(() => {});
    api.get("/admin/spawntap/conversions").then((r) => setConvs(r.data)).catch(() => {});
    api.get("/admin/spawntap/stats").then((r) => setSpawnStats(r.data)).catch(() => {});
    api.get("/admin/adgem/conversions").then((r) => setAgConvs(r.data)).catch(() => {});
    api.get("/admin/adgem/stats").then((r) => setAgStats(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/payouts/${id}/approve`);
      toast.success(data.simulated ? "Payout marked paid (simulated — add PayPal keys for real transfer)" : "PayPal payout sent!");
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reject = async (id) => {
    try { await api.post(`/admin/payouts/${id}/reject`); toast.success("Rejected & refunded"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const approveConv = async (id) => {
    try { await api.post(`/admin/spawntap/conversions/${id}/approve`); toast.success("Conversion approved & credited"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const rejectConv = async (id) => {
    try { await api.post(`/admin/spawntap/conversions/${id}/reject`); toast.success("Conversion rejected"); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const convBadge = { pending: "text-amber-neon border-amber-500/40", approved: "text-lime-neon border-emerald-500/40", rejected: "text-[#FF007A] border-pink-500/40" };
  const agBadge = { credited: "text-lime-neon border-emerald-500/40", rejected: "text-[#FF007A] border-pink-500/40", flagged: "text-[#FF007A] border-pink-500/40", duplicate: "text-amber-neon border-amber-500/40" };

  const statCards = stats ? [
    { label: "Users", value: stats.users, icon: Users, color: "text-cyan-neon" },
    { label: "Pending Payouts", value: stats.pending_payouts, icon: Wallet, color: "text-amber-neon" },
    { label: "Total Paid", value: fmtUsd(stats.total_paid_usd), icon: DollarSign, color: "text-lime-neon" },
    { label: "Coins Outstanding", value: fmtCoins(stats.total_coins_outstanding), icon: Coins, color: "text-amber-neon" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <p className="eyebrow mb-1" style={{ color: "#FF007A" }}>/ CONTROL ROOM</p>
        <h1 className="font-display font-black text-2xl sm:text-3xl">Admin Panel</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {statCards.map((s) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="neon-card p-4">
            <s.icon className={`w-5 h-5 ${s.color}`} />
            <p className="font-mono-num font-bold text-lg mt-2">{s.value}</p>
            <p className="text-xs text-slate-500">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <Tabs defaultValue="payouts">
        <TabsList className="bg-[#0C101A] border border-slate-800">
          <TabsTrigger value="payouts" data-testid="admin-tab-payouts">Payouts</TabsTrigger>
          <TabsTrigger value="spawntap" data-testid="admin-tab-spawntap">Spawntap</TabsTrigger>
          <TabsTrigger value="adgem" data-testid="admin-tab-adgem">AdGem</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
        </TabsList>

        <TabsContent value="payouts" className="mt-4">
          <div className="neon-card p-5">
            {payouts.length === 0 ? <p className="text-slate-500 text-sm py-6 text-center">No payout requests.</p> : (
              <div className="divide-y divide-slate-800/70">
                {payouts.map((p) => (
                  <div key={p.payout_id} className="flex flex-wrap items-center gap-3 py-3" data-testid="admin-payout-row">
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-mono-num font-semibold">{fmtUsd(p.amount_usd)} <span className="text-slate-500 text-xs">· {fmtCoins(p.coins)} coins</span></p>
                      <p className="text-xs text-slate-500">{p.user_email} → {p.paypal_email}</p>
                    </div>
                    <Badge className="bg-transparent border border-slate-700 capitalize">{p.status}</Badge>
                    {p.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => approve(p.payout_id)} data-testid="admin-approve-payout-btn" className="btn-neon h-8">
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(p.payout_id)} data-testid="admin-reject-payout-btn" className="h-8 border-slate-700 text-slate-300">
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {p.batch_id && <span className="text-xs font-mono-num text-slate-500">{p.batch_id}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="spawntap" className="mt-4 space-y-4">
          {spawnStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { l: "Pending", v: spawnStats.pending, c: "text-amber-neon" },
                { l: "Approved", v: spawnStats.approved, c: "text-lime-neon" },
                { l: "Rejected", v: spawnStats.rejected, c: "text-[#FF007A]" },
                { l: "Payout (USD)", v: fmtUsd(spawnStats.total_payout_usd), c: "text-lime-neon" },
                { l: "Coins Credited", v: fmtCoins(spawnStats.total_coins_credited), c: "text-amber-neon" },
              ].map((s) => (
                <div key={s.l} className="neon-card p-4">
                  <p className={`font-mono-num font-bold text-lg ${s.c}`}>{s.v}</p>
                  <p className="text-xs text-slate-500">{s.l}</p>
                </div>
              ))}
            </div>
          )}
          <div className="neon-card p-5">
            <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Gamepad2 className="w-5 h-5 text-cyan-neon" /> Offerwall Conversions</h3>
            {convs.length === 0 ? (
              <p className="text-slate-500 text-sm py-6 text-center">No conversions yet. They appear here when Spawntap sends a postback.</p>
            ) : (
              <div className="divide-y divide-slate-800/70">
                {convs.map((c) => (
                  <div key={c.conversion_id} className="flex flex-wrap items-center gap-3 py-3" data-testid="admin-conversion-row">
                    <div className="flex-1 min-w-[200px]">
                      <p className="font-semibold text-sm flex items-center gap-2">
                        {c.offer_name || c.offer_id}
                        {!c.signature_valid && <ShieldAlert className="w-4 h-4 text-[#FF007A]" title="Invalid signature" />}
                      </p>
                      <p className="text-xs text-slate-500">
                        {c.user_email} · event: {c.event || "—"} · {fmtCoins(c.amount_coins)} coins · {fmtUsd(c.payout_usd)}
                        {c.reason ? ` · ${c.reason}` : ""}
                      </p>
                    </div>
                    <Badge className={`bg-transparent border capitalize ${convBadge[c.status] || "border-slate-700"}`}>{c.status}</Badge>
                    {c.status === "pending" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => approveConv(c.conversion_id)} data-testid="admin-approve-conversion-btn" className="btn-neon h-8">
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => rejectConv(c.conversion_id)} data-testid="admin-reject-conversion-btn" className="h-8 border-slate-700 text-slate-300">
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="adgem" className="mt-4 space-y-4">
          {agStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[
                { l: "Credited", v: agStats.credited, c: "text-lime-neon" },
                { l: "Rejected/Flagged", v: agStats.rejected, c: "text-[#FF007A]" },
                { l: "Duplicates", v: agStats.duplicate, c: "text-amber-neon" },
                { l: "Payout (USD)", v: fmtUsd(agStats.total_payout_usd), c: "text-lime-neon" },
                { l: "Coins Credited", v: fmtCoins(agStats.total_coins_credited), c: "text-amber-neon" },
              ].map((s) => (
                <div key={s.l} className="neon-card p-4">
                  <p className={`font-mono-num font-bold text-lg ${s.c}`}>{s.v}</p>
                  <p className="text-xs text-slate-500">{s.l}</p>
                </div>
              ))}
            </div>
          )}
          <div className="neon-card p-5">
            <h3 className="font-display font-bold mb-3">AdGem Conversions (verified server-side)</h3>
            {agConvs.length === 0 ? (
              <p className="text-slate-500 text-sm py-6 text-center">No AdGem conversions yet. They appear here when AdGem sends a verified postback.</p>
            ) : (
              <div className="divide-y divide-slate-800/70">
                {agConvs.map((c) => (
                  <div key={c.conversion_id} className="flex flex-wrap items-center gap-3 py-3" data-testid="admin-adgem-row">
                    <div className="flex-1 min-w-[220px]">
                      <p className="font-semibold text-sm flex items-center gap-2">
                        {c.offer_name || c.transaction_id || "(conversion)"}
                        {!c.verified && <ShieldAlert className="w-4 h-4 text-[#FF007A]" />}
                      </p>
                      <p className="text-xs text-slate-500">
                        {c.user_email || c.player_id} · {fmtCoins(c.amount)} coins · {fmtUsd(c.payout)} · txn {c.transaction_id || "—"}
                        {c.reason ? ` · ${c.reason}` : ""}{c.ip ? ` · ${c.ip}` : ""}
                      </p>
                    </div>
                    <Badge className={`bg-transparent border capitalize ${agBadge[c.status] || "border-slate-700"}`}>{c.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="neon-card p-5">
            <div className="divide-y divide-slate-800/70">
              {users.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between py-3" data-testid="admin-user-row">
                  <div>
                    <p className="font-semibold text-sm">{u.name || u.email} {u.role === "admin" && <Badge className="ml-1 bg-pink-500/10 text-[#FF007A] border border-pink-500/30">admin</Badge>}</p>
                    <p className="text-xs text-slate-500">{u.email} · {u.auth_provider}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono-num text-sm text-amber-neon">{fmtCoins(u.coins)}</p>
                    <p className="text-xs text-lime-neon font-mono-num">{fmtUsd(u.balance_usd)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
